import { useDispatch } from 'react-redux';
import { useState } from 'react';
import moment from 'moment';

import { APPLICATION_STATUSES } from 'src/common/constants';
import { Box } from 'src/core/components/styled/Box';
import { createErrorNotification, createSuccessNotification } from 'src/core/services/createNotification';
import { currentUserId, currentVendorId } from 'src/vendors/services/currentVendorSelector';
import { dateFormat, timeFormat } from 'src/utils/services/validator';
import { FORWARD_VIEW_ID, VIDEO_DEVICE_SURFSIGHT_TYPE_ID } from 'src/routes/constants';
import { getSpeedInformation } from 'src/common/services/getSpeedInformation';
import { MapGLPopup, MapGLPopupContent, MapGLPopupTitle } from 'src/common/components/map/MapGLPopup';
import { saveVideoRequest } from 'src/routes/ducks';
import { StrobeImagesModalResolver } from 'src/routes/components/modals';
import { time, timeStamp } from 'src/utils/services/formatter';
import { useSelector } from 'src/core/hooks/useSelector';
import { VideoRequestFormValues } from 'src/routes/components/pages/routes/routePageSections/routeMap/vehicleTrackings/VideoRequestForm';
import LabeledDataView from 'src/core/components/LabeledDataView';
import StrobeImagesResolver from 'src/routes/components/pages/routes/routePageSections/routeMap/StrobeImagesResolver';
import translate from 'src/core/services/translate';
import VideoRequestModal from 'src/routes/components/pages/routes/routePageSections/routeMap/vehicleTrackings/VideoRequestModal';

interface Props {
  vehicleId: number;
  vehicleName: string;
  applicationModeStatusId: number;
  speed: number;
  unitOfMeasure: string;
  timestamp?: string;
  showTimestamp?: boolean;
  shouldDisplayDownloadVideo?: boolean;
  videoDeviceTypeId?: number;
  deviceId?: string;
}

const TravelPathVehicleBreadcrumbsPopup: React.FC<Props> = ({
  vehicleId,
  vehicleName,
  applicationModeStatusId,
  speed,
  unitOfMeasure,
  timestamp,
  showTimestamp,
  shouldDisplayDownloadVideo,
  videoDeviceTypeId = VIDEO_DEVICE_SURFSIGHT_TYPE_ID,
  deviceId,
}) => {
  const [isStrobeImageModalOpen, setIsStrobeImageModalOpen] = useState(false);
  const [isVideoRequestModalOpen, setIsVideoRequestModalOpen] = useState(false);

  const dispatch = useDispatch();

  const userId = useSelector(currentUserId);
  const vendorId = useSelector(currentVendorId);

  const onVideoRequestModalSubmit = (formValues: VideoRequestFormValues) => {
    saveVideoRequest({ ...formValues, userId, vendorId })(dispatch)
      .then(() => {
        createSuccessNotification(translate('routes.alertMessages.videoRequestSaved'));
        setIsVideoRequestModalOpen(false);
      })
      .catch(() => {
        createErrorNotification(translate('routes.alertMessages.videoRequestSaveError'));
      });
  };

  return (
    <>
      <MapGLPopup>
        <MapGLPopupTitle title={vehicleName} />

        <MapGLPopupContent>
          {!!timestamp && (
            <LabeledDataView
              width="calc(100% - 10px)"
              size="small"
              label={translate('common.timestamp')}
              value={showTimestamp ? timeStamp(timestamp) : time(timestamp)}
            />
          )}

          {applicationModeStatusId && (
            <LabeledDataView
              width="calc(100% - 10px)"
              size="small"
              label={translate('common.appStatus')}
              value={APPLICATION_STATUSES[applicationModeStatusId]?.name}
            />
          )}
          <LabeledDataView
            width="calc(100% - 10px)"
            size="small"
            label={translate('common.speed')}
            value={getSpeedInformation(speed, unitOfMeasure)}
          />

          <LabeledDataView noWrap width="calc(100% - 10px)" size="small" label={translate('common.images')}>
            <StrobeImagesResolver
              vehicleId={vehicleId}
              timestamp={timestamp}
              setIsStrobeImageModalOpen={setIsStrobeImageModalOpen}
            />
          </LabeledDataView>

          {shouldDisplayDownloadVideo && (
            <LabeledDataView noWrap size="small">
              <Box cursor="pointer" onClick={() => setIsVideoRequestModalOpen(true)} textDecoration="underline">
                {translate('routes.videoRequest.requestVideoFootage')}
              </Box>
            </LabeledDataView>
          )}
        </MapGLPopupContent>
      </MapGLPopup>

      {isStrobeImageModalOpen && (
        <StrobeImagesModalResolver
          vehicleId={vehicleId}
          vehicleName={vehicleName}
          timeStamp={timestamp}
          closeModal={() => setIsStrobeImageModalOpen(false)}
        />
      )}

      {isVideoRequestModalOpen && (
        <VideoRequestModal
          closeModal={() => setIsVideoRequestModalOpen(false)}
          onSubmit={onVideoRequestModalSubmit}
          initialValues={{
            date: moment(timestamp).format(dateFormat),
            time: moment(timestamp).format(timeFormat),
            vehicleId,
            vehicleName,
            vehicleCameraConfigurationTypeIds: [FORWARD_VIEW_ID],
            videoDeviceTypeId,
            deviceId,
          }}
        />
      )}
    </>
  );
};

export default TravelPathVehicleBreadcrumbsPopup;
